import { Skin, Sport } from '#src/__generated__/globalTypes.ts';
import { SetStellar } from '#src/atoms/icons/SetStellar.tsx';

import setBackgroundImage from './assets/background/background.webp';
import seasonCountdownBackgroundDesktop from './assets/desktopNebulaBackground.webp';
import playFootballBackgroundImage from './assets/footballBackground.webp';
import playFootballFieldImage from './assets/footballField.webp';
import seasonCountdownBackgroundMobile from './assets/mobileNebulaBackground.webp';
import playNbaBackgroundImage from './assets/nbaBackground.webp';
import playNbaFieldImage from './assets/nbaField.webp';
import packOpeningBackground from './assets/pack-opening-background.webp';
import replayDialogFootballBackground from './assets/replayDialog/football.webp';
import replayDialogNBABackground from './assets/replayDialog/nba.webp';
import seasonCountdownPacksFootball from './assets/seasonCountdown/packsFootball.webp';
import seasonCountdownPacksNBA from './assets/seasonCountdown/packsNBA.webp';
import seasonCountdownPlayerRightFootball from './assets/seasonCountdown/playerRightFootball.webp';
import seasonCountdownPlayersLeftFootball from './assets/seasonCountdown/playersLeftFootball.webp';
import seasonCountdownPlayersLeftNBA from './assets/seasonCountdown/playersLeftNBA.webp';
import seasonCountdownPlayersMobileFootball from './assets/seasonCountdown/playersMobileFootball.webp';
import seasonCountdownPlayersMobileNBA from './assets/seasonCountdown/playersMobileNBA.webp';
import seasonCountdownPlayersRightNBA from './assets/seasonCountdown/playersRightNBA.webp';
import setLogoImage from './assets/set-logo.svg';
import commonPacksHeaderImage from './assets/stellar-header.webp';
import commonPacksLogoImage from './assets/stellar-logo.svg';
import cardRevealSwipeIconImage from './assets/stellar-series-icon.svg';
import fullArtAv1 from './assets/videos/full-art.av1.webm';
import fullArtH264 from './assets/videos/full-art.h264.mp4';
import fullArtVp9 from './assets/videos/full-art.vp9.webm';
import holoAv1 from './assets/videos/holo.av1.webm';
import holoH264 from './assets/videos/holo.h264.mp4';
import holoVp9 from './assets/videos/holo.vp9.webm';
import jerseyNumberAv1 from './assets/videos/jersey-number.av1.webm';
import jerseyNumberH264 from './assets/videos/jersey-number.h264.mp4';
import jerseyNumberVp9 from './assets/videos/jersey-number.vp9.webm';
import meteorStrikerAv1 from './assets/videos/meteor-striker.av1.webm';
import meteorStrikerH264 from './assets/videos/meteor-striker.h264.mp4';
import meteorStrikerVp9 from './assets/videos/meteor-striker.vp9.webm';
import playVoucherIcon from './assets/voucher.svg';

type SetPlaySportAssets = {
  backgroundImage: string;
  fieldImage: string;
};

const DEFAULT_SET_PLAY_SPORT_ASSETS: SetPlaySportAssets = {
  backgroundImage: playFootballBackgroundImage,
  fieldImage: playFootballFieldImage,
};

const SET_PLAY_SPORT_ASSETS: Partial<Record<Sport, SetPlaySportAssets>> = {
  [Sport.FOOTBALL]: DEFAULT_SET_PLAY_SPORT_ASSETS,
  [Sport.NBA]: {
    backgroundImage: playNbaBackgroundImage,
    fieldImage: playNbaFieldImage,
  },
};
export const SET_STELLAR = {
  slug: 'stellar',
  cardEditionSlugs: [
    'stellar_standard_base',
    'stellar_shiny_base',
    'stellar_shiny_jersey_number',
    'stellar_shiny_meteor_striker',
    'stellar_holo_base',
    'stellar_holo_action_shot',
    'stellar_full_art_base',
    'stellar_full_art_signed',
  ] as const,
  skins: [Skin.STANDARD, Skin.SHINY, Skin.HOLO, Skin.FULL_ART],
  colors: {
    main: 'var(--c-set-stellar-surface-primary)',
    swipeEffectColor: '#7D41FF',
  },
  visual: {
    commonPacks: {
      headerImage: commonPacksHeaderImage,
      logoImage: commonPacksLogoImage,
    },
    cardReveal: {
      swipeIconImage: cardRevealSwipeIconImage,
      background: {
        background: `
      url("${packOpeningBackground}") center / cover no-repeat
        `,
        backgroundBlendMode: 'normal',
      },
      videoSources: {
        holo: [
          { src: holoAv1, type: 'video/webm; codecs="av01.0.31M.08,opus"' },
          { src: holoVp9, type: 'video/webm; codecs="vp9"' },
          { src: holoH264 },
        ],
        jerseyNumber: [
          {
            src: jerseyNumberAv1,
            type: 'video/webm; codecs="av01.0.31M.08,opus"',
          },
          { src: jerseyNumberVp9, type: 'video/webm; codecs="vp9"' },
          { src: jerseyNumberH264 },
        ],
        meteorStriker: [
          {
            src: meteorStrikerAv1,
            type: 'video/webm; codecs="av01.0.31M.08,opus"',
          },
          { src: meteorStrikerVp9, type: 'video/webm; codecs="vp9"' },
          { src: meteorStrikerH264 },
        ],
        fullArt: [
          {
            src: fullArtAv1,
            type: 'video/webm; codecs="av01.0.31M.08,opus"',
          },
          { src: fullArtVp9, type: 'video/webm; codecs="vp9"' },
          { src: fullArtH264 },
        ],
      },
    },
    seasonCountdown: {
      players: {
        [Sport.NBA]: {
          mobile: seasonCountdownPlayersMobileNBA,
          desktopLeft: seasonCountdownPlayersLeftNBA,
          desktopRight: seasonCountdownPlayersRightNBA,
        },
        [Sport.FOOTBALL]: {
          mobile: seasonCountdownPlayersMobileFootball,
          desktopLeft: seasonCountdownPlayersLeftFootball,
          desktopRight: seasonCountdownPlayerRightFootball,
        },
      },
      backgroundMobile: {
        background: `url("${seasonCountdownBackgroundMobile}") center / cover no-repeat`,
      },
      backgroundDesktop: {
        background: `url("${seasonCountdownBackgroundDesktop}") center / cover no-repeat`,
      },
      packs: {
        [Sport.NBA]: seasonCountdownPacksNBA,
        [Sport.FOOTBALL]: seasonCountdownPacksFootball,
      },
    },
    brand: {
      logoImage: setLogoImage,
      SetIcon: SetStellar,
    },
    play: {
      getSportAssets: (sport: Sport) =>
        SET_PLAY_SPORT_ASSETS[sport] || DEFAULT_SET_PLAY_SPORT_ASSETS,
      voucherIcon: playVoucherIcon,
    },
    replayDialog: {
      background: {
        [Sport.FOOTBALL]: replayDialogFootballBackground,
        [Sport.NBA]: replayDialogNBABackground,
        [Sport.BASEBALL]: undefined,
      },
    },
  },
  background: {
    background: `
      url("${setBackgroundImage}") center top / cover
    `,
    backgroundBlendMode: 'normal',
  },
} as const;
